import React, { useEffect, useMemo, useState } from 'react';

import { WineDescription } from '~/components/views/AssetDetails/components/Description';
import { WineCritic } from '~/components/views/AssetDetails/components/WineCritic';
import { useAuthentication } from '~/hooks/useAuthentication';
import { Container } from '~/vinovest-design-system/components';

import ProductHeader from './ProductHeader';
import ProductWrapper from './ProductWrapper';
import type { CMSCritic, CMSTradeVintage, CMSVintageProduct } from '~/networking/api/cms/responses/CMSWineVintage';
import { useProductPrice } from '~/hooks/queries/historical-pricing/useProductPrice';
import { Badges } from '../AssetDetails/components/Badges';
import { RegionInfo } from '../AssetDetails/components/RegionInfo';
import { type BreadcrumbLink, ProductBreadcrumbs } from '../AssetDetails/components/ProductBreadcrumbs';
import { tradeRoutes } from '~/routes/trade';
import { getRegionByName } from '~/networking/api/cms/requests/getRegionByNames';
import { CMSTradeRegion } from '~/networking/api/cms/responses/CMSRegion';
import { GreenButton } from '../TrackRecord/GreenButton';
import { useWindowSize } from '~/hooks/useWindowSize';
import Head from 'next/head';

interface ProductProps {
    lwin11: CMSTradeVintage;
}

interface LoggedOutCtaProps {
    location: string;
    classnames?: string;
}

const LoggedOutCta = ({ location, classnames }: LoggedOutCtaProps) => {
    const { isMobile } = useWindowSize();

    return (
        <div
            className={`flex justify-start items-start max-w-[100%] md:max-w-[200px] mt-[30px] border-b-[1px] md:border-0 pb-[24px] md:pb-0 border-[#DBDBDB] ${classnames}`}
        >
            <GreenButton
                location={location}
                title={'Get Started'}
                width={`${isMobile ? '100%' : '200px'}`}
                height={'56px'}
            />
        </div>
    );
};

const Product = ({ lwin11 }: ProductProps) => {
    const { isMobile } = useWindowSize();
    const { isAuthenticated } = useAuthentication();

    useEffect(() => {
        // for debugging
        // eslint-disable-next-line
        (window.Vino as any).lwin11 = lwin11;
        return () => {
            // don't leak memory
            // eslint-disable-next-line
            (window.Vino as any).lwin11 = undefined;
        };
    });

    const productsSorted = useMemo(
        () =>
            (lwin11.products || [])?.sort((a, b) => {
                if (a.ml === 750 && a.bottles === 6) {
                    return -1;
                }
                if (b.ml === 750 && b.bottles === 6) {
                    return 1;
                }
                const diff = a.ml - b.ml;
                if (diff === 0) {
                    return a.bottles - b.bottles;
                }
                return diff;
            }),
        [lwin11.products]
    );
    const [selectedProduct, setSelectedProduct] = useState<CMSVintageProduct>(productsSorted[0]);

    const { data: productPrice } = useProductPrice(selectedProduct?.id);
    const [regionData, setRegionData] = useState<CMSTradeRegion | undefined>(undefined);

    useEffect(() => {
        if (!lwin11.wineInfo.producerRegion) {
            return;
        }
        void getRegionByName(lwin11.wineInfo.producerRegion).then((response) => setRegionData(response.data));
    }, [lwin11]);

    const breadcrumbPaths: BreadcrumbLink[] = [
        { name: 'Home', href: '/' },
        { name: 'Wine Directory', href: tradeRoutes.wineDirectory.href }
    ];
    if (lwin11.wineInfo.producerCountry) {
        breadcrumbPaths.push({
            name: lwin11.wineInfo.producerCountry,
            href: `${tradeRoutes.wineDirectory.href}/${lwin11.wineInfo.countrySlug}`
        });
    }
    if (regionData?.displayName || lwin11.wineInfo.producerRegion) {
        breadcrumbPaths.push({
            name: (regionData?.displayName || lwin11.wineInfo.producerRegion)!,
            href: `${tradeRoutes.wineDirectory.href}/${lwin11.wineInfo.countrySlug}/${lwin11.wineInfo.regionSlug}`
        });
    }
    breadcrumbPaths.push(
        {
            name: `${lwin11.year} ${lwin11.wineInfo.displayName}`
        } // This is the current page and won't be a link
    );

    const lowestRating = lwin11.critics.reduce((currentScore: number, critic: CMSCritic) => {
        return Number(critic.score) < Number(currentScore) ? Number(critic.score) : Number(currentScore);
    }, 100);

    const highestRating = lwin11.critics.reduce((maxScore: number, critic) => {
        return Number(critic.score) > Number(maxScore) ? Number(critic.score) : maxScore;
    }, 0);

    const averageScore = useMemo(() => {
        if (lwin11.critics.length === 0) return 0;
        const summedValue = lwin11.critics.reduce((initialValue: number, critic) => {
            return initialValue + Number(critic.score);
        }, 0);
        return summedValue / lwin11.critics.length;
    }, []);

    const reviews = lwin11.critics.map((critic) => ({
        '@type': 'CriticReview',
        author: {
            '@type': 'Organization',
            name: critic.name
        },
        reviewBody: critic.description,
        itemReviewed: {
            '@type': 'IndividualProduct',
            additionalType: lwin11.year,
            name: `${lwin11.year} ${lwin11.wineInfo.displayName}`
        },
        reviewRating: {
            '@type': 'Rating',
            worstRating: 1,
            ratingValue: critic.score,
            bestRating: 100,
            reviewAspect: 'Tasting Note'
        }
    }));

    return (
        <>
            <Head>
                {/* Product Schema */}
                <script
                    type={'application/ld+json'}
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org/',
                            '@type': 'Product',
                            name: `${lwin11.year} ${lwin11.wineInfo.displayName}`,
                            description: lwin11.description,
                            countryOfOrigin: {
                                '@type': 'Country',
                                name: lwin11.wineInfo.producerCountry
                            },
                            model: lwin11.year,
                            brand: {
                                '@type': 'Brand',
                                name: lwin11.wineInfo.producerName
                            },
                            sku: lwin11.lwin11.slice(0, 7),
                            image: lwin11.bottleImage?.url,
                            aggregateRating: {
                                '@type': 'AggregateRating',
                                worstRating: lowestRating,
                                ratingCount: lwin11.critics.length,
                                ratingValue: averageScore,
                                bestRating: highestRating
                            },
                            review: [reviews]
                        })
                    }}
                />

                {/* Breadcrumb Schema */}
                <script
                    type={'application/ld+json'}
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'BreadcrumbList',
                            itemListElement: [
                                {
                                    '@type': 'ListItem',
                                    position: 1,
                                    name: 'Home',
                                    item: 'https://www.vinovest.co/'
                                },
                                {
                                    '@type': 'ListItem',
                                    position: 2,
                                    name: 'Wine Directory',
                                    item: 'https://www.vinovest.co/trade'
                                },
                                {
                                    '@type': 'ListItem',
                                    position: 3,
                                    name: lwin11.wineInfo.producerCountry,
                                    item: `https://www.vinovest.co${tradeRoutes.wineDirectory.href}/${lwin11.wineInfo.countrySlug}`
                                },
                                {
                                    '@type': 'ListItem',
                                    position: 4,
                                    name: regionData?.displayName || lwin11.wineInfo.producerRegion,
                                    item: `https://www.vinovest.co${tradeRoutes.wineDirectory.href}/${lwin11.wineInfo.countrySlug}/${lwin11.wineInfo.regionSlug}`
                                },
                                {
                                    '@type': 'ListItem',
                                    position: 5,
                                    name: `${lwin11.year} ${lwin11.wineInfo.displayName}`
                                }
                            ]
                        })
                    }}
                />
            </Head>
            <Container classnames={'max-w-[933px]'}>
                {!isMobile && <ProductBreadcrumbs paths={breadcrumbPaths} />}
                <div className={'flex flex-col lg:mb-10'}>
                    <ProductHeader
                        displayName={lwin11.wineInfo.displayName}
                        vintage={lwin11.year}
                        selectedProduct={selectedProduct}
                        handleBottleSizeClick={setSelectedProduct}
                        productPrice={productPrice}
                        products={productsSorted}
                        ticker={lwin11.wineInfo.producers?.ticker}
                    />
                    <ProductWrapper vintage={lwin11} isAuthenticated={isAuthenticated} />
                </div>
                {lwin11.badges.length > 0 && <Badges badges={lwin11.badges} />}
                {lwin11.description && (
                    <>
                        <WineDescription description={lwin11.description} />
                        {!isAuthenticated && <LoggedOutCta location={'why_buying'} classnames={'mb-[65px]'} />}
                    </>
                )}
                {lwin11.critics.length > 0 && (
                    <>
                        <WineCritic wineCritic={lwin11.critics} />
                        {!isAuthenticated && <LoggedOutCta location={'critic_scores'} classnames={'mb-[55px]'} />}
                    </>
                )}
                {regionData && (
                    <>
                        <RegionInfo region={regionData} />
                        {!isAuthenticated && regionData.description && (
                            <LoggedOutCta location={'region_summary'} classnames={'mb-[65px] relative top-[-190px]'} />
                        )}
                    </>
                )}
                {isMobile && <ProductBreadcrumbs paths={breadcrumbPaths} />}
            </Container>
        </>
    );
};

export default Product;
